import {
  dedupeJobTitleValues,
  fetchAllJobTitles,
  mapJobTitleValues,
} from './job-title-options.shared';
import { OnOptionsLoad } from './types';

/** Synthetic autocomplete value; stripped before API requests. */
export const ALL_HEALTHCARE_JOB_TITLE_ID = -4;

export const ALL_HEALTHCARE_JOB_TITLE_OPTION: {
  id: number;
  name: string;
} = {
  id: ALL_HEALTHCARE_JOB_TITLE_ID,
  name: 'All Healthcare',
};

/** Healthcare job title IDs for "All Healthcare". */
export const HEALTHCARE_JOB_TITLE_IDS = [
  14, // Doctor
  15, // Psychologist/Counselor/Therapist
  44, // Other patient/nursing home resident
  46, // Massage therapist
  57, // Medical staff
  69, // Nurse
  70, // Adult psychiatric patient perpetrator
  71, // CNA-Certified Nursing Assistant,-health care worker
  72, // Nurse's Assistant
  73, // Physician's Assistant
  76, // EMT (Emergency Medical Technician)
  78, // Physical Therapist
] as const;

export const HEALTHCARE_JOB_TITLE_NAMES = [
  'Doctor',
  'Psychologist/Counselor/Therapist',
  'Other patient/nursing home resident',
  'Massage therapist',
  'Medical staff',
  'Nurse',
  'Adult psychiatric patient perpetrator',
  'CNA-Certified Nursing Assistant,-health care worker',
  "Nurse's Assistant",
  "Physician's Assistant",
  'EMT (Emergency Medical Technician)',
  'Physical Therapist',
] as const;

const normalizedHealthcareJobTitleIds = new Set<number>(
  HEALTHCARE_JOB_TITLE_IDS,
);

const normalizedHealthcareJobTitleNames = new Set(
  HEALTHCARE_JOB_TITLE_NAMES.map((name) => name.trim().toLowerCase()),
);

export const isAllHealthcareJobTitleOption = (
  item: { id?: number | string } | null | undefined,
): boolean => Number(item?.id) === ALL_HEALTHCARE_JOB_TITLE_ID;

export const isHealthcareJobTitle = (jobTitle: {
  id?: number | string;
  name?: string;
}): boolean => {
  const id = Number(jobTitle.id);

  if (Number.isFinite(id) && normalizedHealthcareJobTitleIds.has(id)) {
    return true;
  }

  if (!jobTitle.name) {
    return false;
  }

  return normalizedHealthcareJobTitleNames.has(
    jobTitle.name.trim().toLowerCase(),
  );
};

export async function loadHealthcareJobTitles(
  onOptionsLoad: OnOptionsLoad,
): Promise<Array<{ id: number; name: string }>> {
  const allJobTitles = await fetchAllJobTitles(onOptionsLoad);

  return mapJobTitleValues(
    allJobTitles.filter((jobTitle) => isHealthcareJobTitle(jobTitle)),
  );
}

export async function resolveAllHealthcareJobTitles(
  onOptionsLoad: OnOptionsLoad,
  currentValue: Array<{ id: number; name: string }> | undefined,
): Promise<Array<{ id: number; name: string }>> {
  const healthcareJobTitles = await loadHealthcareJobTitles(onOptionsLoad);

  const withoutAllHealthcare = (currentValue ?? [])
    .filter((item) => !isAllHealthcareJobTitleOption(item))
    .map((item) => ({
      id: typeof item.id === 'number' ? item.id : Number(item.id),
      name: item.name ?? '',
    }));

  return dedupeJobTitleValues([
    ...withoutAllHealthcare,
    ...healthcareJobTitles,
  ]);
}
